import React, { useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import { ActivationStatus, FetchStatus, FetchType } from '../../../enums';
import { UploadTwoToneIcon } from './assets/upload-two-tone.icon';
import { IPeopleHeaderActionsCMSLayoutProps } from './people.cms.models';
import { BulkActionsMenu } from './bulk-actions-menu/bulk-actions-menu';
import { ColumnConfiguration } from '../../../models/column-configuration';
import { filterableColumns } from './people.cms.helpers';
import { AddTwoToneIcon, Button, Checkbox, clearFilter, DropdownOption, DropdownSearch, DropdownSelect, DropdownSelectOption, Filters, IconButton, Modal, Searchfield, selectFilter, Tooltip, useMatchScreenWidth } from '@keplerco/core';
import { generateDropdownOptionsFromEntities } from '../../../library/helpers/generate-dropdown-items-from-unique-entities';
import { useNavigate, useParams } from 'react-router';
import { PagePath } from '../../../navigation/navigation.enums';
import { ConfirmationModalLayout } from '../../../widgets/layouts';
import { BulkActionModalPayload } from './bulk-actions-menu/bulk-actions-menu.models';
import { FlagIcon } from '../../../design/icons/flag.icon';
import styles from './people.module.css';

export function PeopleHeaderActionsCMSLayout(props: IPeopleHeaderActionsCMSLayoutProps): JSX.Element {
  const { departmentSlug, teamSlug } = useParams();

  const { fetchState, companyVariables } = useAppState();
  const actions = useAppActions();
  const navigate = useNavigate();

  const [bulkActionModalState, setBulkActionModalState] = useState<BulkActionModalPayload>({
    selectedPeople: undefined,
    showModal: false,
  });

  const showIconButton = useMatchScreenWidth(1360);

  const [selectedDepartment, setSelectedDepartment] = useState<string>();
  const [departmentOptions, setDepartmentOptions] = useState<DropdownSelectOption[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>();
  const [teamOptions, setTeamOptions] = useState<DropdownOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>();
  const [countryOptions, setCountryOptions] = useState<DropdownOption[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>();
  const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    async function generateFilterByColumn(column: ColumnConfiguration) {
      if (!column.key || !column.label || !filterableColumns.includes(column.key)) return;

      switch (column.key) {
        case 'Department': {
          const options = generateDropdownOptionsFromEntities(props.departments, (value: string) => setDepartmentOptions(currentState => selectFilter(currentState, value)));
          setDepartmentOptions(options);
          break;
        }

        case 'Team': {
          const options = generateDropdownOptionsFromEntities(props.teams, (value: string) => setTeamOptions(currentState => selectFilter(currentState, value)));
          setTeamOptions(options);
          break;
        }

        case 'Country': {
          const options: DropdownOption[] = [];

          props.countries?.forEach(country => {
            if (!options.some(option => option.value === country.slug)) {
              options.push({
                label: country.name,
                value: country.slug!,
                icon: <FlagIcon isoCode={country.iso} />,
                onClick: () => setCountryOptions(currentState => selectFilter(currentState, country.slug!)),
              });
            }
          });

          setCountryOptions(options);
          break;
        }

        case 'Status': {
          const options = generateDropdownOptionsFromEntities(
            [
              { slug: ActivationStatus.Active.toString(), name: 'Account active' },
              { slug: ActivationStatus.Archived.toString(), name: 'Archived' },
              { slug: ActivationStatus.Inactive.toString(), name: 'Activation required' },
              { slug: ActivationStatus.NotInvited.toString(), name: 'Invite pending' },
            ],
            (value: string) => setStatusOptions(currentState => selectFilter(currentState, value))
          );
          setStatusOptions(options);
          break;
        }

        default: {
          break;
        }
      }
    }

    props.columnConfiguration.forEach(generateFilterByColumn);
  }, [props.columnConfiguration, props.departments, props.teams, props.countries]);

  async function onInputHandler(value: string) {
    actions.startLoader({ path: props.path, type: FetchType.Custom });
    props.setRequest({ ...props.request, search: value, page: 1 });
  }

  function clearFilters() {
    const nextRequest = structuredClone(props.request);
    nextRequest.page = 1;
    nextRequest.departmentSlug = departmentSlug;
    nextRequest.teamSlug = teamSlug;
    nextRequest.countryId = undefined;
    nextRequest.activationStatuses = undefined;
    props.setRequest(nextRequest);

    setSelectedDepartment(undefined);
    setDepartmentOptions(currentState => clearFilter(currentState));
    setSelectedTeam(undefined);
    setTeamOptions(currentState => clearFilter(currentState));
    setSelectedCountry(undefined);
    setCountryOptions(currentState => clearFilter(currentState));
    setSelectedStatus(undefined);
    setStatusOptions(currentState => clearFilter(currentState));
  }

  function applyFilters() {
    const nextRequest = structuredClone(props.request);
    nextRequest.page = 1;
    nextRequest.departmentSlug = selectedDepartment;
    nextRequest.teamSlug = selectedTeam;
    nextRequest.countryId = !selectedCountry ? undefined : parseInt(selectedCountry);
    nextRequest.activationStatuses = !selectedStatus ? undefined : [parseInt(selectedStatus)];

    props.setRequest(nextRequest);
  }

  async function handleBulkArchiveAction(actionType: 'archive' | 'unarchive', selectedPeople: string[]) {
    actions.startLoader({ path: PagePath.userManagementPeople, type: FetchType.Sending });

    if (actionType === 'archive') {
      await actions.bulkArchivePeople(selectedPeople);
    } else if (actionType === 'unarchive') {
      await actions.bulkUnarchivePeople(selectedPeople);
    }

    setBulkActionModalState({ selectedPeople: undefined, showModal: false });
    applyFilters();
    actions.stopLoader(PagePath.userManagementPeople);
  }

  if (props.isMobile)
    return (
      <React.Fragment>
        <div className={styles.peopleActions}>
          <div>
            <Filters onClickClear={clearFilters} onClickApply={applyFilters}>
              {!departmentSlug && !teamSlug && <DropdownSelect label="Department" options={departmentOptions} value={selectedDepartment} onChange={event => setSelectedDepartment(event.target.value)} />}

              {!teamSlug && <DropdownSelect label="Team" options={teamOptions} value={selectedTeam} onChange={event => setSelectedTeam(event.target.value)} />}

              <DropdownSearch label="Country" options={countryOptions} value={selectedCountry} onChange={event => setSelectedCountry(event.target.value)} />

              <DropdownSelect label="Status" options={statusOptions} value={selectedStatus} onChange={event => setSelectedStatus(event.target.value)} />
            </Filters>
          </div>

          <div>
            <Tooltip content="Bulk Upload">
              <IconButton iconType="fill" borderColour="borders" backgroundColour="cards" borderRadius="10px" borderWidth={1} onClick={() => navigate(`${PagePath.userManagementBase}${PagePath.userManagementPeopleBulkUpload.replace(':companySlug', companyVariables.slug!)}`)}>
                <UploadTwoToneIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div>
            <Tooltip content="Add A Person">
              <IconButton iconType="fill" borderColour="borders" backgroundColour="cards" borderRadius="10px" borderWidth={1} onClick={() => props.setPersonToManage({ show: true })}>
                <AddTwoToneIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div style={{ width: '100%' }}>
            <Searchfield loading={fetchState[props.path].status === FetchStatus.Active && fetchState[props.path].type === FetchType.Custom} responsive onInput={onInputHandler} />
          </div>
        </div>

        <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 15 }}>
          <Checkbox
            checked={props.selectedRows.length === props.data?.employees.length}
            id="selectAll"
            onChange={() => {
              if (!props.data?.employees) return;
              const slugs: any[] = props.data.employees.map(person => ({ slug: person.learnerSlug! }));
              props.setSelectedRows(props.selectedRows.length === props.data?.employees.length ? [] : slugs);
            }}
          >
            Select all
          </Checkbox>

          {props.selectedRows.length > 0 && (
            <BulkActionsMenu
              handleBulkActionModalState={setBulkActionModalState}
              path={props.path}
              selectedRows={props.selectedRows}
              setSelectedRows={props.setSelectedRows}
              data={props.data}
              onClickSendActivationInvitation={props.onClickSendActivationInvitation}
              onClickSendActivationReminder={props.onClickSendActivationReminder}
            />
          )}
        </div>
      </React.Fragment>
    );

  return (
    <React.Fragment>
      <div className={styles.peopleActions}>
        <div>
          <Searchfield loading={fetchState[props.path].status === FetchStatus.Active && fetchState[props.path].type === FetchType.Custom} onInput={onInputHandler} />
        </div>

        <div>
          <Filters onClickClear={clearFilters} onClickApply={applyFilters}>
            {!departmentSlug && !teamSlug && <DropdownSelect label="Department" options={departmentOptions} value={selectedDepartment} onChange={event => setSelectedDepartment(event.target.value)} />}

            {!teamSlug && <DropdownSelect label="Team" options={teamOptions} value={selectedTeam} onChange={event => setSelectedTeam(event.target.value)} />}

            <DropdownSearch label="Country" options={countryOptions} value={selectedCountry} onChange={event => setSelectedCountry(event.target.value)} />

            <DropdownSelect label="Status" options={statusOptions} value={selectedStatus} onChange={event => setSelectedStatus(event.target.value)} />
          </Filters>
        </div>

        <div>
          {showIconButton ? (
            <Tooltip content="Bulk Upload">
              <IconButton iconType="fill" borderColour="borders" backgroundColour="cards" borderRadius="10px" borderWidth={1} onClick={() => navigate(`${PagePath.userManagementBase}${PagePath.userManagementPeopleBulkUpload.replace(':companySlug', companyVariables.slug!)}`)}>
                <UploadTwoToneIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Button type="button" square theme="dark" onClick={() => navigate(`${PagePath.userManagementBase}${PagePath.userManagementPeopleBulkUpload.replace(':companySlug', companyVariables.slug!)}`)}>
                <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                  <UploadTwoToneIcon />
                  Bulk Upload
                </div>
              </Button>
            </>
          )}
        </div>

        <div>
          {showIconButton ? (
            <Tooltip content="Add A Person">
              <IconButton iconType="fill" borderColour="borders" backgroundColour="cards" borderRadius="10px" borderWidth={1} onClick={() => props.setPersonToManage({ show: true })}>
                <AddTwoToneIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button type="button" square theme="dark" onClick={() => props.setPersonToManage({ show: true })}>
              <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                <AddTwoToneIcon />
                Add A Person
              </div>
            </Button>
          )}
        </div>

        <div>
          <BulkActionsMenu
            handleBulkActionModalState={setBulkActionModalState}
            path={props.path}
            selectedRows={props.selectedRows}
            setSelectedRows={props.setSelectedRows}
            data={props.data}
            onClickSendActivationInvitation={props.onClickSendActivationInvitation}
            onClickSendActivationReminder={props.onClickSendActivationReminder}
          />
        </div>
      </div>

      <Modal open={bulkActionModalState.showModal} onClose={() => setBulkActionModalState({ selectedPeople: undefined, showModal: false })}>
        <ConfirmationModalLayout
          textAlign="left"
          titleSize="standard"
          onClickCancel={() => setBulkActionModalState({ selectedPeople: undefined, showModal: false })}
          onClickSubmit={() => {
            const { actionType, selectedPeople } = bulkActionModalState;
            if (selectedPeople && (actionType === 'archive' || actionType === 'unarchive')) {
              handleBulkArchiveAction(actionType, selectedPeople);
            }
          }}
          title={bulkActionModalState.actionType === 'archive' ? 'Archive users' : 'Unarchive users'}
          subtitle={`Are you sure you want to ${bulkActionModalState.actionType === 'archive' ? 'archive' : 'unarchive'} ${bulkActionModalState.selectedPeople && bulkActionModalState.selectedPeople?.length > 0 && bulkActionModalState?.selectedPeople.length} people?`}
          submitButtonText={`Yes, ${bulkActionModalState.actionType === 'archive' ? 'archive' : 'unarchive'}`}
          cancelButtonText="No, cancel"
        />
      </Modal>
    </React.Fragment>
  );
}
